package com.siriusxm.pia

/**
 * The UCP (AVP / Cedar) question an application allows access by, or null when it sets no
 * [ApplicationDescriptor.allowedCapability] (UCA-1181). Action-only: no resource or context.
 */
internal fun ApplicationDescriptor.capabilityQuery(): CapabilityQuery? =
    allowedCapability?.let { CapabilityQuery(CapabilityAction(it)) }

/**
 * Decides whether a viewer may use an application (UCA-1181). Pure: the viewer's group membership
 * and capability answers come in as functions, so the rule can be tested without a login.
 *
 * - No [ApplicationDescriptor.requiredGroup] and no [ApplicationDescriptor.allowedCapability]:
 *   unrestricted, everyone gets in.
 * - Otherwise the viewer needs to be in one of the required groups, or to hold the allowed
 *   capability. An application that sets only a capability is therefore open to exactly the
 *   users that hold it.
 *
 * Hide-only: this is a UI affordance, and the application's own services enforce access.
 *
 * @param descriptor the application being checked
 * @param isInGroup whether the viewer is in the named Cognito group
 * @param isCapabilityAllowed whether the viewer is known to hold the capability; must read false for
 *        a capability that has not been resolved, so access fails closed
 */
internal fun applicationAccessAllowed(
    descriptor: ApplicationDescriptor,
    isInGroup: (String) -> Boolean,
    isCapabilityAllowed: (CapabilityQuery) -> Boolean
): Boolean {
    val groups = descriptor.requiredGroup
    val capability = descriptor.capabilityQuery()
    if (groups.isNullOrEmpty() && capability == null) return true
    return groups.orEmpty().any(isInGroup) ||
            (capability != null && isCapabilityAllowed(capability))
}
